import { Trans } from 'react-i18next';

import {
   HealthcareProviderAppointmentCancellationPatientNotificationConfigurationQuery,
   HealthcareProviderAppointmentCancellationPatientNotificationEnabledSetCommand,
} from '@dreryk/egabinet-phone-notification-frontend-api';

import { refetchCqrsQuery, useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { useFormController } from '@dreryk/react-common';
import {
   dispatchSnackbarPositiveEvent,
   Form,
   IconFillColor,
   IconSize,
   IconWrapper,
   QuestionmarkCircleIcon,
   StyledFlex,
   Toggle,
   Tooltip,
} from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { patientPhoneNotificationTranslationKeys } from '../../translations';

import { PatientPhoneNotificationAppointmentCancellationToggleLoader } from './PatientPhoneNotificationAppointmentCancellationToggle.loader';
import { PhoneNotificationTooltipText } from './PhoneNotificationTooltipText';

const SAVED_SNACKBAR_DELAY_IN_MS = 5000;

const useCurrentHealthcareProviderAppointmentCancellationPatientNotificationEnabledSetCommand = (): UseCqrsCommandResult<
   void,
   PatientPhoneNotificationAppointmentCancellationToggleFormBean
> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new HealthcareProviderAppointmentCancellationPatientNotificationEnabledSetCommand();
         command.enabled = Choice.of(parameters.notificationEnabled ?? false);
         return command;
      },
      {
         onSuccess: (ignore, parameters) => {
            dispatchSnackbarPositiveEvent(
               parameters.notificationEnabled
                  ? patientPhoneNotificationTranslationKeys.notificationToggle
                       .appointmentPatientCancellationToggleSaveOn
                  : patientPhoneNotificationTranslationKeys.notificationToggle
                       .appointmentPatientCancellationToggleSaveOff,
               {
                  autoHideDelayInMs: SAVED_SNACKBAR_DELAY_IN_MS,
               }
            );
            refetchCqrsQuery(HealthcareProviderAppointmentCancellationPatientNotificationConfigurationQuery);
         },
      }
   );
};

type PatientPhoneNotificationAppointmentCancellationToggleFormBean = {
   notificationEnabled: boolean;
};

export const PatientPhoneNotificationAppointmentCancellationToggle = (props: {
   readonly notificationEnabled: boolean;
   readonly messages: string[];
}): JSX.Element | null => {
   const { register, handleSubmit } = useFormController<PatientPhoneNotificationAppointmentCancellationToggleFormBean>(
      {
         notificationEnabled: props.notificationEnabled,
      },
      { mode: 'onChange' }
   );
   const {
      executeAsync: enablePatientNotificationSet,
      loading,
   } = useCurrentHealthcareProviderAppointmentCancellationPatientNotificationEnabledSetCommand();

   return (
      <StyledFlex gap={'12px'}>
         <Form onInput={handleSubmit(enablePatientNotificationSet)}>
            <Toggle
               controller={register('notificationEnabled')}
               testId={'patientPhoneNotificationAppointmentCancellationToggle'}
               disabled={loading}
            />
         </Form>
         <StyledFlex gap={'4px'} alignItems={'center'}>
            <Trans
               i18nKey={patientPhoneNotificationTranslationKeys.notificationToggle.appointmentPatientCancellationToggle}
            />
            <Tooltip
               maxWidth={'600px'}
               testId={'appointmentPatientCancellationToggleTooltip'}
               content={
                  <PhoneNotificationTooltipText
                     header={
                        patientPhoneNotificationTranslationKeys.notificationToggle
                           .appointmentPatientCancellationToggleTooltip
                     }
                     messages={props.messages}
                  />
               }
            >
               <IconWrapper
                  testId={'appointmentPatientCancellationToggleTooltipIconWrapper'}
                  fillColor={IconFillColor.PRIMARY}
                  size={IconSize.SMALL}
               >
                  <QuestionmarkCircleIcon />
               </IconWrapper>
            </Tooltip>
         </StyledFlex>
      </StyledFlex>
   );
};

export const PatientPhoneNotificationAppointmentCancellationToggleWithLoader = (): JSX.Element => {
   return (
      <PatientPhoneNotificationAppointmentCancellationToggleLoader>
         {({ healthcareProviderNotificationConfiguration }) => (
            <PatientPhoneNotificationAppointmentCancellationToggle
               notificationEnabled={healthcareProviderNotificationConfiguration.enabled}
               messages={healthcareProviderNotificationConfiguration.messages}
            />
         )}
      </PatientPhoneNotificationAppointmentCancellationToggleLoader>
   );
};
